import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useStore } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Receipt,
  ReceiptText,
  ChevronDown,
  ChevronUp,
  Banknote,
  Smartphone,
  CreditCard,
  ShoppingCart,
} from "lucide-react";
import { hasRole } from "@/lib/auth-store";
import type { Sale } from "@/lib/pos-store";

export const Route = createFileRoute("/receipts")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "cashier")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "Receipts" },
      {
        name: "description",
        content: "Browse all sales receipts.",
      },
    ],
  }),
  component: ReceiptsPage,
});

/** A grouped receipt from direct checkout (sales sharing a receiptId) */
type CheckoutReceipt = {
  type: "checkout";
  receiptId: string;
  date: number;
  payment: string;
  sales: Sale[];
  total: number;
};

/** An individual sale from tab issuance (no receiptId) */
type TabReceipt = {
  type: "tab";
  sale: Sale;
};

type ReceiptEntry = CheckoutReceipt | TabReceipt;

function ReceiptsPage() {
  const sales = useStore((s) => s.sales);
  const items = useStore((s) => s.items);
  const [searchQ, setSearchQ] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<"all" | "checkout" | "tab">(
    "all",
  );

  // Build receipt entries
  const entries = useMemo(() => {
    const result: ReceiptEntry[] = [];

    // Group sales by receiptId (checkout receipts)
    const receiptMap = new Map<string, Sale[]>();
    const ungrouped: Sale[] = [];

    for (const sale of sales) {
      if (sale.receiptId) {
        const existing = receiptMap.get(sale.receiptId);
        if (existing) {
          existing.push(sale);
        } else {
          receiptMap.set(sale.receiptId, [sale]);
        }
      } else {
        ungrouped.push(sale);
      }
    }

    // Convert grouped receipts
    for (const [receiptId, receiptSales] of receiptMap) {
      const sorted = [...receiptSales].sort(
        (a, b) => Number(a.at) - Number(b.at),
      );
      result.push({
        type: "checkout",
        receiptId,
        date: Number(sorted[0].at),
        payment: sorted[0].payment,
        sales: sorted,
        total: sorted.reduce((a, s) => a + s.total, 0),
      });
    }

    // Add tab-issued sales individually
    for (const sale of ungrouped) {
      result.push({
        type: "tab",
        sale,
      });
    }

    // Sort by date, newest first
    result.sort((a, b) => {
      const dateA = a.type === "checkout" ? a.date : Number(a.sale.at);
      const dateB = b.type === "checkout" ? b.date : Number(b.sale.at);
      return dateB - dateA;
    });

    return result;
  }, [sales]);

  // Filter entries
  const filtered = useMemo(() => {
    return entries
      .filter((e) => {
        if (filterType === "checkout") return e.type === "checkout";
        if (filterType === "tab") return e.type === "tab";
        return true;
      })
      .filter((e) => {
        if (!searchQ) return true;
        const q = searchQ.toLowerCase();
        if (e.type === "checkout") {
          return e.receiptId.toLowerCase().includes(q);
        }
        const item = items.find((i) => i.id === e.sale.itemId);
        return (
          item?.name.toLowerCase().includes(q) ||
          e.sale.receiptId?.toLowerCase().includes(q)
        );
      });
  }, [entries, searchQ, filterType, items]);

  const checkoutCount = entries.filter((e) => e.type === "checkout").length;
  const tabCount = entries.filter((e) => e.type === "tab").length;

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case "cash":
        return <Banknote className="h-3.5 w-3.5 text-muted-foreground" />;
      case "mpesa":
        return <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />;
      default:
        return <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  return (
    <div>
      <PageHeader
        title="Receipts"
        subtitle={`${entries.length} receipt${entries.length !== 1 ? "s" : ""}`}
      />

      {/* Filter tabs */}
      <div className="flex gap-1.5 px-4 pt-3">
        <button
          onClick={() => setFilterType("all")}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium transition ${
            filterType === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground"
          }`}
        >
          All ({entries.length})
        </button>
        <button
          onClick={() => setFilterType("checkout")}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium transition ${
            filterType === "checkout"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground"
          }`}
        >
          Sales ({checkoutCount})
        </button>
        <button
          onClick={() => setFilterType("tab")}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium transition ${
            filterType === "tab"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground"
          }`}
        >
          Tab ({tabCount})
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search receipt or item..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>
      </div>

      {/* Receipt list */}
      <div className="space-y-2 px-4 pt-4 pb-24">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <Receipt className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">
              No Receipts
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQ
                ? "No receipts match your search"
                : "Receipts will appear here after sales"}
            </p>
          </div>
        ) : (
          filtered.map((entry) => {
            const entryId =
              entry.type === "checkout"
                ? `checkout-${entry.receiptId}`
                : `tab-${entry.sale.id}`;
            const isExpanded = expandedId === entryId;

            if (entry.type === "checkout") {
              return (
                <div
                  key={entryId}
                  className="rounded-xl border border-border bg-card overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : entryId)}
                    className="w-full p-3 text-left active:scale-[0.98] min-h-[56px]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <ReceiptText className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          Receipt #{entry.receiptId.slice(-6).toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          {new Date(Number(entry.date)).toLocaleDateString(
                            "en-KE",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                          {" · "}
                          {new Date(Number(entry.date)).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-sm font-semibold text-primary">
                          {fmt(entry.total)}
                        </p>
                        <div className="flex items-center justify-end gap-1">
                          {getPaymentIcon(entry.payment)}
                          <span className="text-[10px] text-muted-foreground capitalize">
                            {entry.payment}
                          </span>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-border px-3 py-3 space-y-2">
                      <div className="divide-y divide-border rounded-lg border border-border">
                        {entry.sales.map((s) => {
                          const item = items.find((i) => i.id === s.itemId);
                          return (
                            <div
                              key={s.id}
                              className="flex items-center gap-2 px-2 py-1.5"
                            >
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-accent text-[10px] font-bold text-accent-foreground">
                                {Number(s.qty)}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium">
                                  {item?.name ?? "—"}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  {Number(s.qty)} × {fmt(Number(s.unitPrice))}
                                </p>
                              </div>
                              <p className="text-xs font-semibold">
                                {fmt(Number(s.total))}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-end pt-1">
                        <Link
                          to="/receipt/$id"
                          params={{ id: entry.receiptId }}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs gap-1"
                          >
                            View Receipt →
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Tab receipt (individual sale)
            const item = items.find((i) => i.id === entry.sale.itemId);
            return (
              <div
                key={entryId}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : entryId)}
                  className="w-full p-3 text-left active:scale-[0.98] min-h-[56px]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground">
                      <ShoppingCart className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {item?.name ?? "Unknown Item"}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {new Date(Number(entry.sale.at)).toLocaleDateString(
                          "en-KE",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                        {" · "}
                        {new Date(Number(entry.sale.at)).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-sm font-semibold text-primary">
                        {fmt(Number(entry.sale.total))}
                      </p>
                      <div className="flex items-center justify-end gap-1">
                        {getPaymentIcon(entry.sale.payment)}
                        <span className="text-[10px] text-muted-foreground capitalize">
                          {entry.sale.payment}
                        </span>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-border px-3 py-3">
                    <div className="rounded-lg border border-border bg-card p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          Quantity
                        </span>
                        <span className="text-xs font-medium">
                          {Number(entry.sale.qty)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground">
                          Unit Price
                        </span>
                        <span className="text-xs font-medium">
                          {fmt(Number(entry.sale.unitPrice))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground">
                          Total
                        </span>
                        <span className="text-xs font-semibold">
                          {fmt(Number(entry.sale.total))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
